import { formatLocalDateTime, toTimeValue } from "./dateTime";
import { sanitizeImportedSourceText } from "./importTextSanitizers";

function safeString(value) {
  return sanitizeImportedSourceText(String(value ?? "")).trim();
}

function getActivityMetadata(activity) {
  return activity?.metadata && typeof activity.metadata === "object"
    ? activity.metadata
    : {};
}

function getActivityTimestamp(activity) {
  const metadata = getActivityMetadata(activity);
  return (
    activity?.created_at ||
    activity?.createdAt ||
    activity?.occurred_at ||
    activity?.occurredAt ||
    activity?.timestamp ||
    activity?.date ||
    activity?.updated_at ||
    activity?.updatedAt ||
    metadata?.created_at ||
    metadata?.createdAt ||
    metadata?.occurred_at ||
    metadata?.occurredAt ||
    metadata?.timestamp ||
    metadata?.date ||
    ""
  );
}

function getJobCandidateRejectionContext(activity) {
  const metadata = getActivityMetadata(activity);
  const jobCandidateAction = safeString(
    metadata.job_candidate_action || metadata.jobCandidateAction
  ).toLowerCase();
  const nextStage = safeString(
    metadata.next_stage || metadata.nextStage
  ).toLowerCase();
  const rawTitle = safeString(activity?.title).toLowerCase();

  return {
    isRejected:
      jobCandidateAction === "job_candidate_rejected" ||
      nextStage === "rejected" ||
      rawTitle === "candidate rejected",
    rejectedBy: safeString(metadata.rejected_by || metadata.rejectedBy),
    rejectionReason: safeString(
      metadata.rejection_reason || metadata.rejectionReason
    ),
    rejectionNote: safeString(
      metadata.rejection_note ||
        metadata.rejectionNote ||
        metadata.note ||
        metadata.notes
    ),
  };
}

function buildRejectedActivityBody(activity) {
  const details = safeString(activity?.details || activity?.summary);
  const { rejectedBy, rejectionReason, rejectionNote } =
    getJobCandidateRejectionContext(activity);
  const lines = details
    ? details
        .split(/\r?\n/)
        .map((line) => safeString(line))
        .filter(Boolean)
    : [];
  const detailsBlob = `\n${lines.join("\n")}\n`.toLowerCase();

  if (rejectedBy && !detailsBlob.includes(`\nrejected by: ${rejectedBy.toLowerCase()}\n`)) {
    lines.push(`Rejected by: ${rejectedBy}`);
  }

  if (
    rejectionReason &&
    !detailsBlob.includes(`\nreason: ${rejectionReason.toLowerCase()}\n`)
  ) {
    lines.push(`Reason: ${rejectionReason}`);
  }

  if (
    rejectionNote &&
    !detailsBlob.includes(`\nnotes: ${rejectionNote.toLowerCase()}\n`)
  ) {
    lines.push(`Notes: ${rejectionNote}`);
  }

  return lines.join("\n");
}

function buildActivityBody(activity) {
  const { isRejected } = getJobCandidateRejectionContext(activity);
  if (isRejected) {
    return buildRejectedActivityBody(activity);
  }

  return safeString(activity?.details || activity?.summary);
}

function normalizeActivityType(value) {
  const normalized = safeString(value).toLowerCase();
  if (normalized === "email" || normalized === "mail") return "email";
  if (normalized === "note" || normalized === "comment") return "note";
  return "action";
}

function resolveBusinessFriendlyActivityTitle(activity) {
  const metadata = getActivityMetadata(activity);
  const rawTitle = safeString(activity?.title);
  const normalizedTitle = rawTitle.toLowerCase();
  const quickActionKey = safeString(
    metadata.quick_action_key || metadata.quickActionKey
  ).toLowerCase();
  const jobCandidateAction = safeString(metadata.job_candidate_action).toLowerCase();
  const nextStage = safeString(metadata.next_stage || metadata.nextStage).toLowerCase();

  if (
    quickActionKey === "match_candidates" ||
    normalizedTitle === "match candidates quick action"
  ) {
    return "Candidate matching started";
  }

  if (
    jobCandidateAction === "added_to_job" ||
    normalizedTitle === "added to job" ||
    normalizedTitle === "candidate added to job"
  ) {
    if (normalizedTitle === "added to job") {
      return "Job added to Candidate";
    }

    return "Candidate added to Job";
  }

  if (
    jobCandidateAction === "job_candidate_rejected" ||
    nextStage === "rejected" ||
    normalizedTitle === "candidate rejected"
  ) {
    return "Candidate rejected";
  }

  return rawTitle;
}

function formatTypeLabel(type) {
  const normalized = normalizeActivityType(type);
  if (normalized === "email") return "Email";
  if (normalized === "note") return "Note";
  return "Action";
}

export function formatActivityDateTime(value) {
  if (!value) return "";
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return formatLocalDateTime(value, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: browserTimeZone,
  }, safeString(value));
}

function openAttachment(url) {
  const href = safeString(url);
  if (!href || typeof window === "undefined") return;
  window.open(href, "_blank", "noopener,noreferrer");
}

function buildAttachmentItems(attachments) {
  if (!Array.isArray(attachments)) return [];

  return attachments
    .map((attachment) => {
      if (!attachment || typeof attachment !== "object") return null;

      const label = safeString(
        attachment.label ||
          attachment.name ||
          attachment.filename ||
          attachment.original_name
      );
      const url = safeString(attachment.url || attachment.href);

      if (!label && !url) return null;

      return {
        label: label || "Attachment",
        onClick: url ? () => openAttachment(url) : undefined,
      };
    })
    .filter(Boolean);
}

function shouldUseUpdatedByLabel(activity) {
  const metadata =
    activity?.metadata && typeof activity.metadata === "object"
      ? activity.metadata
      : {};
  const changedFieldCount = Number(
    metadata.changed_field_count ?? metadata.change_count ?? 0
  );

  if (Number.isFinite(changedFieldCount) && changedFieldCount > 0) {
    return true;
  }

  const title = safeString(activity?.title).toLowerCase();
  const summary = safeString(activity?.summary).toLowerCase();
  const details = safeString(activity?.details).toLowerCase();
  const blob = `${title} ${summary} ${details}`;

  return (
    /\b(updated|changed|edited|refreshed)\b/.test(blob) ||
    /\bstatus changed\b/.test(blob) ||
    /\bstage changed\b/.test(blob)
  );
}

function getActorLabel(activity) {
  return shouldUseUpdatedByLabel(activity) ? "Updated by:" : "Added by:";
}

function buildCandidateMeta(activity) {
  const metadata = getActivityMetadata(activity);
  const type = normalizeActivityType(activity?.activity_type || activity?.type);
  const meta = [];

  const actorName = safeString(activity?.actor_name || metadata.actor_name || metadata.actor);
  const summary = safeString(activity?.summary);

  if (type === "email") {
    const fromEmail = safeString(metadata.from_email || metadata.from);
    const toEmail = safeString(metadata.to_email || metadata.to || metadata.email);
    const subject = safeString(metadata.subject || metadata.topic || activity?.summary);

    if (fromEmail) meta.push({ label: "From:", value: fromEmail });
    if (toEmail) meta.push({ label: "To:", value: toEmail });
    if (subject) meta.push({ label: "Subject:", value: subject });
  } else {
    if (summary && summary !== safeString(activity?.details)) {
      meta.push({ value: summary });
    }

    if (actorName) {
      meta.push({ label: getActorLabel(activity), value: actorName });
    }
  }

  return meta;
}

export function toCandidateActivityItem(activity) {
  if (!activity || typeof activity !== "object") return null;

  const details = buildActivityBody(activity);

  return {
    raw: activity,
    id: activity.id || `activity-${Date.now()}`,
    type: normalizeActivityType(activity.activity_type || activity.type),
    title:
      resolveBusinessFriendlyActivityTitle(activity) ||
      formatTypeLabel(activity.activity_type),
    dateText: formatActivityDateTime(getActivityTimestamp(activity)),
    ts: toTimeValue(getActivityTimestamp(activity)) || null,
    meta: buildCandidateMeta(activity),
    body: details,
    attachments: buildAttachmentItems(activity.attachments),
    showReadMore: false,
    showEdit: false,
    showDelete: false,
  };
}

export function toLeadActivityItem(activity) {
  if (!activity || typeof activity !== "object") return null;

  const type = normalizeActivityType(activity.activity_type || activity.type);
  const metadata =
    activity?.metadata && typeof activity.metadata === "object"
      ? activity.metadata
      : {};
  const details = safeString(activity.details || activity.summary);
  const summary = safeString(activity.summary);
  const actorName = safeString(activity.actor_name || metadata.actor_name || metadata.actor);
  const attachments = buildAttachmentItems(activity.attachments);
  const numericId = Number(activity.id);
  const isPersisted = Number.isFinite(numericId) && numericId > 0;
  const source = safeString(metadata.source || metadata.note_source);
  const meta = [];

  if (type === "email") {
    const fromEmail = safeString(metadata.from_email || metadata.from || actorName);
    const toEmail = safeString(metadata.to_email || metadata.to || metadata.email);
    const subject = safeString(metadata.subject || metadata.topic || summary);

    if (fromEmail) meta.push({ label: "From:", value: fromEmail });
    if (toEmail) meta.push({ label: "To:", value: toEmail });
    if (subject) meta.push({ label: "Subject:", value: subject });
  } else {
    if (summary && summary !== details) {
      meta.push({ value: summary });
    }

    if (actorName) {
      meta.push({ label: getActorLabel(activity), value: actorName });
    }
  }

  return {
    id: activity.id || `activity-${Date.now()}`,
    type,
    typeLabel: formatTypeLabel(type),
    title: resolveBusinessFriendlyActivityTitle(activity) || formatTypeLabel(type),
    dateText: formatActivityDateTime(getActivityTimestamp(activity)),
    ts: toTimeValue(getActivityTimestamp(activity)) || null,
    meta,
    body: details,
    attachments,
    showReadMore: type === "email" || details.length > 260 || attachments.length > 0,
    showEdit: isPersisted && type !== "email" && source !== "messages",
    showDelete: isPersisted,
    raw: activity,
  };
}

function buildJobMeta(activity) {
  const metadata = getActivityMetadata(activity);
  const type = normalizeActivityType(activity?.activity_type || activity?.type);
  const actorName = safeString(activity?.actor_name || metadata.actor_name || metadata.actor);
  const summary = safeString(activity?.summary);
  const details = buildActivityBody(activity);
  const { rejectionReason } = getJobCandidateRejectionContext(activity);

  if (type === "email") {
    const parts = [];
    const toEmail = safeString(metadata.to_email || metadata.to || metadata.email);
    const subject = safeString(metadata.subject || metadata.topic || activity?.summary);
    if (toEmail) parts.push(`To: ${toEmail}`);
    if (subject) parts.push(`Subject: ${subject}`);
    if (actorName) parts.push(`${getActorLabel(activity)} ${actorName}`);

    return parts.join(" • ");
  }

  const parts = [];

  if (summary && summary !== details) {
    parts.push(summary);
  }

  if (
    rejectionReason &&
    !parts.some((part) => part.toLowerCase().includes(rejectionReason.toLowerCase()))
  ) {
    parts.push(`Reason: ${rejectionReason}`);
  }

  if (actorName) {
    parts.push(`${getActorLabel(activity)} ${actorName}`);
  }

  return parts.join(" • ");
}

export function toJobActivityItem(activity) {
  if (!activity || typeof activity !== "object") return null;

  const attachments = buildAttachmentItems(activity.attachments);
  const body = buildActivityBody(activity);

  return {
    raw: activity,
    id: activity.id || `activity-${Date.now()}`,
    type: formatTypeLabel(activity.activity_type || activity.type),
    title:
      resolveBusinessFriendlyActivityTitle(activity) ||
      formatTypeLabel(activity.activity_type),
    meta: buildJobMeta(activity),
    body,
    attachment: attachments[0]?.label || "",
    actions: [],
    showTrash: false,
    time: formatActivityDateTime(getActivityTimestamp(activity)),
    ts: toTimeValue(getActivityTimestamp(activity)) || null,
  };
}
